import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { ArrowRight, DatabaseBackup, History, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  devRefreshApply,
  devRefreshPreview,
  schemaTransferApply,
  schemaTransferPreview,
  schemaTransferRollback,
  schemaTransfers,
} from '@/shared/api/generated/content/content';
import type { ChangeBody, TransferBody } from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { ConfirmDialog } from '@/shared/ui/Dialog';
import { Segmented } from '@/shared/ui/Segmented';
import { toast } from '@/shared/ui/Toast';

import { FieldInput } from '../RecordPage/FieldInput';
import type { FormField } from '../RecordPage/formModel';
import { kindTitle } from '../SchemaPage/kinds';

import {
  answersFor,
  attrTitle,
  attrValue,
  byType,
  toggle,
  unanswered,
  type Answer,
} from './transferModel';
import styles from './TransferPage.module.scss';

type Direction = 'up' | 'down';

const SIDES: Record<
  Direction,
  { from: 'development' | 'production'; to: 'development' | 'production' }
> = {
  up: { from: 'development', to: 'production' },
  down: { from: 'production', to: 'development' },
};

const ACTIONS: Record<
  ChangeBody['action'],
  { label: string; tone: 'success' | 'warning' | 'danger' }
> = {
  add: { label: 'новое', tone: 'success' },
  update: { label: 'изменено', tone: 'warning' },
  remove: { label: 'убрать', tone: 'danger' },
};

/**
 * Перенос схемы между окружениями (docs/research/04-environments.md).
 *
 * Схема прода руками не правится: её меняют на деве и переносят сюда.
 * Экран показывает разницу, задаёт вопросы там, где без человека не решить,
 * — чем заполнить новое обязательное поле у живых записей, согласен ли он
 * отправить тип в корзину, — и переносит отмеченное одной транзакцией.
 * Каждый перенос можно откатить: перед ним снимается снимок схемы.
 */
export function TransferPage() {
  const { siteId } = useParams({ from: '/authed/sites/$siteId/$env/transfer' });
  const queryClient = useQueryClient();
  const [direction, setDirection] = useState<Direction>('up');
  const sides = SIDES[direction];

  const preview = useQuery({
    queryKey: ['schema-transfer', siteId, sides.from, sides.to],
    queryFn: async () => {
      const response = await schemaTransferPreview(siteId, sides);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос разницы схем');
      }

      return response.data;
    },
  });

  const history = useQuery({
    queryKey: ['schema-transfers', siteId],
    queryFn: async () => {
      const response = await schemaTransfers(siteId);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на запрос истории переносов');
      }

      return response.data.items ?? [];
    },
  });

  // Выбор живёт вместе с версиями схем: разница пересчиталась — выбор
  // начинается заново, а не тянет отметки изменений, которых больше нет.
  const key = `${preview.data?.from_version ?? ''}:${preview.data?.to_version ?? ''}`;
  const changes = preview.data?.changes ?? [];
  const [state, setState] = useState<{
    key: string;
    selected: Set<string>;
    answers: Record<string, Answer>;
  }>();
  const current =
    state?.key === key
      ? state
      : { key, selected: new Set(changes.map((change) => change.id)), answers: {} };

  const [confirming, setConfirming] = useState(false);
  const [rollingBack, setRollingBack] = useState<TransferBody>();

  const open = unanswered(changes, current.selected, current.answers);
  const count = current.selected.size;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['schema-transfer', siteId] });
    void queryClient.invalidateQueries({ queryKey: ['schema-transfers', siteId] });
    void queryClient.invalidateQueries({ queryKey: ['content-types', siteId] });
    void queryClient.invalidateQueries({ queryKey: ['content-type'] });
    void queryClient.invalidateQueries({ queryKey: ['block-types', siteId] });
    void queryClient.invalidateQueries({ queryKey: ['records'] });
  };

  const apply = async () => {
    if (preview.data === undefined) {
      return;
    }

    try {
      await schemaTransferApply(siteId, {
        ...sides,
        from_version: preview.data.from_version,
        to_version: preview.data.to_version,
        changes: [...current.selected],
        answers: answersFor(changes, current.selected, current.answers),
      });
      toast.success(`Перенесено на ${envTitle(sides.to)}`, {
        description: 'Если что-то пошло не так, перенос можно откатить ниже.',
      });
    } catch (cause) {
      toast.error(
        cause instanceof ApiError && cause.status === 412
          ? 'Схема успела поменяться'
          : 'Не удалось перенести',
        {
          description:
            cause instanceof ApiError && cause.status === 412
              ? 'Разница пересчитана — посмотрите её ещё раз.'
              : cause instanceof ApiError
                ? cause.detail
                : 'Попробуйте ещё раз.',
        },
      );

      throw cause;
    } finally {
      refresh();
    }
  };

  return (
    <div className={styles['page']}>
      <header className={styles['head']}>
        <h1 className={styles['title']}>Перенос схемы</h1>
        <p className={styles['subtitle']}>
          Схему меняют на Development и переносят на Production — отмеченными изменениями, одной
          транзакцией. Перед переносом снимается снимок: перенос можно откатить.
        </p>
      </header>

      <Segmented
        aria-label="Направление переноса"
        value={direction}
        onValueChange={(value) => {
          setDirection(value === 'down' ? 'down' : 'up');
        }}
        options={[
          { value: 'up', label: 'Development → Production' },
          { value: 'down', label: 'Production → Development' },
        ]}
      />

      {direction === 'down' && (
        <>
          <RefreshCard siteID={siteId} onDone={refresh} />
          <p className={styles['note']} role="note">
            Ниже — только схема: обратный перенос подтягивает схему дева к проду, то, что поменяли
            на деве и не перенесли, из неё пропадёт.
          </p>
        </>
      )}

      {preview.isPending ? (
        <p className={styles['muted']}>Сравниваем схемы…</p>
      ) : preview.isError ? (
        <p className={styles['muted']}>
          {preview.error instanceof ApiError ? preview.error.detail : 'Не удалось сравнить схемы.'}
        </p>
      ) : changes.length === 0 ? (
        <div className={styles['same']}>
          <p>
            Схемы совпадают: на {envTitle(sides.to)} уже то же, что на {envTitle(sides.from)}.
          </p>
        </div>
      ) : (
        <>
          <div className={styles['groups']}>
            {byType(changes).map((group) => (
              <section key={group.typeID} className={styles['group']}>
                <h2 className={styles['group__title']}>
                  {group.name}
                  <Badge size="sm" variant="outline">
                    {kindTitle(group.kind)}
                  </Badge>
                </h2>

                <ul className={styles['changes']}>
                  {group.changes.map((change) => (
                    <ChangeRow
                      key={change.id}
                      change={change}
                      all={changes}
                      siteID={siteId}
                      checked={current.selected.has(change.id)}
                      answer={current.answers[change.id]}
                      onToggle={() => {
                        setState({
                          ...current,
                          selected: toggle(changes, current.selected, change.id),
                        });
                      }}
                      onAnswer={(answer) => {
                        setState({
                          ...current,
                          answers: { ...current.answers, [change.id]: answer },
                        });
                      }}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <footer className={styles['footer']}>
            <span className={styles['muted']}>
              {count === 0
                ? 'Ничего не отмечено'
                : open.length > 0
                  ? `Ответьте на вопросы: ${open.map((change) => `«${change.name}»`).join(', ')}`
                  : `Отмечено изменений: ${String(count)} из ${String(changes.length)}`}
            </span>
            <Button
              iconRight={<ArrowRight size={15} />}
              disabled={count === 0 || open.length > 0}
              onClick={() => {
                setConfirming(true);
              }}
            >
              Перенести на {envTitle(sides.to)}
            </Button>
          </footer>
        </>
      )}

      <section className={styles['history']}>
        <h2 className={styles['history__title']}>
          <History size={16} />
          История переносов
        </h2>

        {(history.data ?? []).length === 0 ? (
          <p className={styles['muted']}>Переносов ещё не было.</p>
        ) : (
          <ul className={styles['history__list']}>
            {(history.data ?? []).map((item) => (
              <li key={item.id} className={styles['history__item']}>
                <div className={styles['history__main']}>
                  <span>
                    {envTitle(item.from)} → {envTitle(item.to)}
                  </span>
                  <span className={styles['muted']}>
                    {when(item.created_at)}
                    {item.created_by !== '' && ` · ${item.created_by}`} · {summaryLine(item)}
                  </span>
                </div>

                {item.rolled_back_at !== undefined ? (
                  <Badge size="sm" tone="neutral">
                    откачен
                  </Badge>
                ) : (
                  item.can_rollback && (
                    <Button
                      size="sm"
                      variant="ghost"
                      tone="neutral"
                      iconLeft={<RotateCcw size={14} />}
                      onClick={() => {
                        setRollingBack(item);
                      }}
                    >
                      Откатить
                    </Button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Перенести на ${envTitle(sides.to)}?`}
        description={`Изменений: ${String(count)}. Схема ${envTitle(sides.to)} поменяется сразу${
          sides.to === 'production' ? ' — формы записей и API сайта увидят её сразу же' : ''
        }. Перенос можно будет откатить.`}
        confirmLabel="Перенести"
        tone="accent"
        onConfirm={apply}
      />

      <ConfirmDialog
        open={rollingBack !== undefined}
        onOpenChange={(value) => {
          if (!value) {
            setRollingBack(undefined);
          }
        }}
        title="Откатить перенос?"
        description={`Схема ${envTitle(rollingBack?.to ?? 'production')} вернётся к тому, что было до переноса. Типы и поля, заведённые им, уйдут в корзину и скроются, а прежние значения записей вернутся там, где их с тех пор не правили.`}
        confirmLabel="Откатить"
        tone="danger"
        onConfirm={async () => {
          if (rollingBack === undefined) {
            return;
          }

          try {
            await schemaTransferRollback(rollingBack.id);
            toast.success('Перенос откачен');
          } catch (cause) {
            toast.error('Не удалось откатить', {
              description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
            });

            throw cause;
          } finally {
            refresh();
          }
        }}
      />
    </div>
  );
}

/**
 * «Обновить Development данными Production»: схема, файлы и записи прода
 * целиком. Записи дева без пары на проде уходят в корзину — об этом
 * говорится прямо, с числом.
 */
function RefreshCard({ siteID, onDone }: { siteID: string; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false);

  const preview = useQuery({
    queryKey: ['schema-transfer', siteID, 'dev-refresh'],
    queryFn: async () => {
      const response = await devRefreshPreview(siteID);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на предпросмотр обновления');
      }

      return response.data;
    },
  });

  const data = preview.data;

  return (
    <section className={styles['refresh']}>
      <div className={styles['refresh__main']}>
        <h2 className={styles['refresh__title']}>
          <DatabaseBackup size={16} />
          Обновить Development целиком
        </h2>
        <p className={styles['muted']}>
          Схема, файлы и записи Production — на Development: дев станет копией прода, чтобы
          пробовать на настоящих данных.
        </p>
        {data !== undefined && (
          <p className={styles['muted']}>
            Схема: изменений {data.schema}. Записи: появится {data.create}, станет как на проде{' '}
            {data.update}
            {data.trash > 0 && <b>, уйдёт в корзину {data.trash}</b>}. Файлов появится {data.media}.
          </p>
        )}
      </div>
      <Button
        variant="outline"
        tone="neutral"
        disabled={data === undefined}
        onClick={() => {
          setConfirming(true);
        }}
      >
        Обновить
      </Button>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Обновить Development данными Production?"
        description={
          data === undefined
            ? ''
            : `Правки Development, которых нет на Production, пропадут: схема станет как на проде, ${
                data.trash > 0
                  ? `${String(data.trash)} записей без пары на проде уйдут в корзину, `
                  : ''
              }записи прода заменят свои копии на деве. Вернуть записи можно из корзины.`
        }
        confirmLabel="Обновить"
        tone="danger"
        onConfirm={async () => {
          try {
            await devRefreshApply(siteID, { confirm: true });
            toast.success('Development обновлён данными Production');
          } catch (cause) {
            toast.error('Не удалось обновить', {
              description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
            });

            throw cause;
          } finally {
            onDone();
          }
        }}
      />
    </section>
  );
}

function ChangeRow({
  change,
  all,
  siteID,
  checked,
  answer,
  onToggle,
  onAnswer,
}: {
  change: ChangeBody;
  all: ChangeBody[];
  siteID: string;
  checked: boolean;
  answer: Answer | undefined;
  onToggle: () => void;
  onAnswer: (answer: Answer) => void;
}) {
  const action = ACTIONS[change.action];
  const label =
    change.target === 'type' ? 'Тип целиком' : change.name.split(' · ').slice(1).join(' · ');
  const requires = (change.requires ?? [])
    .map((id) => all.find((item) => item.id === id)?.name)
    .filter((name): name is string => name !== undefined);

  return (
    <li className={`${styles['change'] ?? ''} ${checked ? '' : (styles['change--off'] ?? '')}`}>
      <div className={styles['change__head']}>
        <Checkbox checked={checked} onCheckedChange={onToggle}>
          <span className={styles['change__name']}>{label}</span>
        </Checkbox>
        <Badge size="sm" tone={action.tone}>
          {action.label}
        </Badge>
        {change.field !== undefined && (
          <span className={styles['change__key']}>{change.field}</span>
        )}
      </div>

      {(change.attrs ?? []).length > 0 && (
        <ul className={styles['attrs']}>
          {(change.attrs ?? []).map((attr) => {
            const before = attrValue(attr.key, attr.before);
            const after = attrValue(attr.key, attr.after);

            return (
              <li key={attr.key}>
                {attrTitle(attr.key)}
                {before !== undefined && after !== undefined && (
                  <>
                    : <s>{before}</s> → <b>{after}</b>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {change.impact.message !== '' && <p className={styles['impact']}>{change.impact.message}</p>}

      {requires.length > 0 && (
        <p className={styles['muted']}>Переносится только вместе с: {requires.join(', ')}</p>
      )}

      {checked && change.question === 'default' && change.default_field !== undefined && (
        <div className={styles['question']}>
          <FieldInput
            field={defaultField(change)}
            value={answer?.value}
            errorKey={change.default_field.key}
            errors={{}}
            siteID={siteID}
            onChange={(value) => {
              onAnswer({ value, confirm: answer?.confirm ?? false });
            }}
          />
          <Checkbox
            checked={answer?.confirm ?? false}
            onCheckedChange={(value) => {
              onAnswer({ ...(answer ?? {}), confirm: value === true });
            }}
          >
            Оставить пустыми — поправлю записи сам
          </Checkbox>
        </div>
      )}

      {checked && change.question === 'confirm' && (
        <div className={styles['question']}>
          <Checkbox
            checked={answer?.confirm ?? false}
            onCheckedChange={(value) => {
              onAnswer({ confirm: value === true });
            }}
          >
            Понимаю последствия, переносить
          </Checkbox>
        </div>
      )}
    </li>
  );
}

/** Поле для ввода значения по умолчанию — то же, что в форме записи. */
function defaultField(change: ChangeBody): FormField {
  const field = change.default_field;

  return {
    id: change.id,
    key: field?.key ?? '',
    name: `Значение для пустых записей`,
    type: field?.type ?? 'string',
    required: false,
    hint: '',
    options: field?.options ?? {},
    widget: '',
    rules: {},
    showIf: undefined,
    width: 'full',
    tab: '',
    subfields: [],
    translatable: false,
  };
}

function envTitle(kind: string): string {
  return kind === 'production' ? 'Production' : 'Development';
}

const dateFormat = new Intl.DateTimeFormat('ru', { dateStyle: 'medium', timeStyle: 'short' });

function when(value: string): string {
  return dateFormat.format(new Date(value));
}

function summaryLine(item: TransferBody): string {
  const counts = (item.summary['counts'] ?? {}) as Record<string, number | undefined>;
  const parts = [
    counts['add'] ? `новых ${String(counts['add'])}` : '',
    counts['update'] ? `изменено ${String(counts['update'])}` : '',
    counts['remove'] ? `убрано ${String(counts['remove'])}` : '',
  ].filter((part) => part !== '');

  return parts.length === 0 ? 'без изменений' : parts.join(', ');
}
